import type {
  Dashboard,
  BrandingKind,
  BrandingUploadStartResponse,
  AiReview,
  AiReviewScope,
  ContentExtraction,
  Ebook,
  EbookDisplayMode,
  EbookFont,
  EbookPublication,
  EbookStorage,
  EbookListResponse,
  MediaOverlays,
  MediaOverlayInput,
  MediaOverlayUploadStartResponse,
  EbookPagesResponse,
  EbookPageSearchResponse,
  EbookPageText,
  PlanId,
  Processing,
  ReaderControls,
  Settings,
  TableOfContents,
  ReaderNavigationItem,
  UploadStartResponse,
  User,
  BillingOverview,
  PaidPlanId,
  PaymentUploadStartResponse,
  PlanEntitlement,
  PaymentRequest,
  AdminUser,
  AdminPayment,
  PreviewTokenResponse,
  EbookRuntimeConfig,
} from "@ebook/contracts";

let csrfToken = "";

export class ApiClientError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch("/api/auth/csrf", { credentials: "include" });
  const data = await response.json() as { csrfToken: string };
  csrfToken = data.csrfToken;
  return csrfToken;
}

async function request<T>(
  path: string,
  options: RequestInit & { mutation?: boolean } = {},
): Promise<T> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const headers = new Headers(options.headers);
    if (options.body) headers.set("content-type", "application/json");
    if (options.mutation) headers.set("x-csrf-token", await ensureCsrf());
    const response = await fetch(path, {
      ...options,
      headers,
      credentials: "include",
    });
    if (response.status === 204) return undefined as T;
    const data = await response.json().catch(() => null) as
      | T
      | { error?: { code?: string; message?: string } }
      | null;
    if (response.ok) return data as T;

    const error = data && typeof data === "object" && "error" in data ? data.error : undefined;
    if (
      options.mutation
      && attempt === 0
      && response.status === 401
      && error?.code === "INVALID_CSRF"
    ) {
      csrfToken = "";
      continue;
    }
    if (response.status === 401) csrfToken = "";
    throw new ApiClientError(
      error?.message ?? "Request failed",
      response.status,
      error?.code ?? "REQUEST_FAILED",
    );
  }
  throw new ApiClientError("Request failed", 401, "INVALID_CSRF");
}

function downloadFileName(disposition: string): string {
  const encoded = disposition.match(/(?:^|;)\s*filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall through to the ASCII fallback.
    }
  }
  const quoted = disposition.match(/(?:^|;)\s*filename="((?:\\.|[^"])*)"/i)?.[1];
  if (quoted) return quoted.replace(/\\(["\\])/g, "$1");
  return "ebook.zip";
}

export const api = {
  async createSession(credential: string): Promise<User> {
    const response = await request<{ user: User; csrfToken: string }>("/api/auth/session", {
      method: "POST",
      mutation: true,
      body: JSON.stringify({ credential }),
    });
    csrfToken = response.csrfToken;
    return response.user;
  },
  me: () => request<{ user: User }>("/api/auth/me").then((response) => response.user),
  updatePlan: (plan: PlanId) =>
    request<{ user: User }>("/api/plan", {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify({ plan }),
    }).then((response) => response.user),
  entitlement: () => request<PlanEntitlement>("/api/entitlement"),
  billing: () => request<BillingOverview>("/api/billing"),
  startPayment: (input: { plan: PaidPlanId; fileName: string; contentType: string; size: number }) =>
    request<PaymentUploadStartResponse>("/api/payments", {
      method: "POST", mutation: true, body: JSON.stringify(input),
    }),
  completePayment: (requestId: string, objectKey: string) =>
    request<{ request: PaymentRequest; entitlement: PlanEntitlement }>(`/api/payments/${requestId}/complete`, {
      method: "POST", mutation: true, body: JSON.stringify({ objectKey }),
    }),
  adminUsers: (query = "") => request<{ items: AdminUser[]; page: number; pageSize: number; total: number }>(`/api/admin/users${query}`),
  adminUpdateUserPlan: (userId: string, plan: PlanId, expiresAt: string | null) =>
    request<void>(`/api/admin/users/${userId}/plan`, {
      method: "PATCH", mutation: true, body: JSON.stringify({ plan, expiresAt }),
    }),
  adminPayments: (query = "") => request<{ items: AdminPayment[]; page: number; pageSize: number; total: number }>(`/api/admin/payments${query}`),
  adminPaymentSlip: (requestId: string) => request<{ url: string; expiresIn: number }>(`/api/admin/payments/${requestId}/slip`),
  approvePayment: (requestId: string) => request<PaymentRequest>(`/api/admin/payments/${requestId}/approve`, { method: "POST", mutation: true }),
  rejectPayment: (requestId: string, reason: string) => request<PaymentRequest>(`/api/admin/payments/${requestId}/reject`, {
    method: "POST", mutation: true, body: JSON.stringify({ reason }),
  }),
  logout: () => request<void>("/api/auth/logout", { method: "POST", mutation: true }),
  dashboard: () => request<Dashboard>("/api/dashboard"),
  profile: () => request<{ user: User }>("/api/profile").then((response) => response.user),
  updateProfile: (displayName: string) =>
    request<{ user: User }>("/api/profile", {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify({ displayName }),
    }).then((response) => response.user),
  settings: () => request<Settings>("/api/settings"),
  updateSettings: (
    settings: Pick<Settings, "locale" | "theme" | "openAiModel"> & {
      openAiApiKey?: string;
      clearOpenAiApiKey?: boolean;
    },
  ) =>
    request<Settings>("/api/settings", {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify(settings),
    }),
  testOpenAi: () =>
    request<{ ok: true; model: Settings["openAiModel"] }>("/api/settings/openai/test", {
      method: "POST",
      mutation: true,
    }),
  ebooks: (query = "") => request<EbookListResponse>(`/api/ebooks${query}`),
  ebook: (id: string) => request<Ebook>(`/api/ebooks/${id}`),
  ebookStorage: (id: string) => request<EbookStorage>(`/api/ebooks/${id}/storage`),
  createEbook: (title: string) =>
    request<Ebook>("/api/ebooks", {
      method: "POST",
      mutation: true,
      body: JSON.stringify({ title }),
    }),
  updateEbook: (id: string, input: {
    title?: string;
    description?: string;
    copyrightText?: string;
    pdfDownloadName?: string | null;
    backgroundColor?: string;
    fontFamily?: EbookFont;
    displayMode?: EbookDisplayMode;
  }) =>
    request<Ebook>(`/api/ebooks/${id}`, {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify(input),
    }),
  updateReaderControls: (id: string, controls: ReaderControls) =>
    request<Ebook>(`/api/ebooks/${id}/reader-controls`, {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify(controls),
    }),
  publication: (id: string) =>
    request<EbookPublication>(`/api/ebooks/${id}/publication`),
  updatePublication: (id: string, projectName: string) =>
    request<EbookPublication>(`/api/ebooks/${id}/publication`, {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify({ projectName }),
    }),
  publish: (id: string) =>
    request<EbookPublication>(`/api/ebooks/${id}/publish`, {
      method: "POST",
      mutation: true,
      headers: { "idempotency-key": crypto.randomUUID() },
    }),
  previewToken: (id: string) =>
    request<PreviewTokenResponse>(`/api/ebooks/${id}/preview-token`, {
      method: "POST",
      mutation: true,
    }),
  previewConfig: (id: string, token: string) =>
    request<EbookRuntimeConfig>(
      `/api/ebooks/${id}/preview-config?token=${encodeURIComponent(token)}`,
    ),
  startBrandingUpload: (
    id: string,
    kind: BrandingKind,
    input: { fileName: string; contentType: string; size: number },
  ) =>
    request<BrandingUploadStartResponse>(`/api/ebooks/${id}/branding/${kind}`, {
      method: "POST",
      mutation: true,
      body: JSON.stringify(input),
    }),
  completeBrandingUpload: (id: string, kind: BrandingKind, objectKey: string) =>
    request<Ebook>(`/api/ebooks/${id}/branding/${kind}/complete`, {
      method: "POST",
      mutation: true,
      body: JSON.stringify({ objectKey }),
    }),
  clearBrandingAsset: (id: string, kind: BrandingKind) =>
    request<Ebook>(`/api/ebooks/${id}/branding/${kind}`, {
      method: "DELETE",
      mutation: true,
    }),
  async downloadEbook(id: string): Promise<{ blob: Blob; fileName: string }> {
    const response = await fetch(`/api/ebooks/${id}/download`, {
      credentials: "include",
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null) as
        | { error?: { code?: string; message?: string } }
        | null;
      throw new ApiClientError(
        data?.error?.message ?? "Download failed",
        response.status,
        data?.error?.code ?? "DOWNLOAD_FAILED",
      );
    }
    const disposition = response.headers.get("content-disposition") ?? "";
    const fileName = downloadFileName(disposition);
    return { blob: await response.blob(), fileName };
  },
  deleteEbook: (id: string) =>
    request<void>(`/api/ebooks/${id}`, { method: "DELETE", mutation: true }),
  pages: (id: string, page = 1, pageSize = 100) =>
    request<EbookPagesResponse>(
      `/api/ebooks/${id}/pages?page=${page}&pageSize=${pageSize}`,
    ),
  contentExtraction: (id: string) =>
    request<ContentExtraction>(`/api/ebooks/${id}/content-extraction`),
  extractContent: (id: string) =>
    request<ContentExtraction>(`/api/ebooks/${id}/content-extraction`, {
      method: "POST",
      mutation: true,
      headers: { "idempotency-key": crypto.randomUUID() },
    }),
  tableOfContents: (id: string) =>
    request<TableOfContents>(`/api/ebooks/${id}/table-of-contents`),
  updateTableOfContents: (
    id: string,
    items: ReaderNavigationItem[] | null,
    expectedRevision: number,
  ) =>
    request<TableOfContents>(`/api/ebooks/${id}/table-of-contents`, {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify({ items, expectedRevision }),
    }),
  pageText: (id: string, pageNumber: number) =>
    request<EbookPageText>(`/api/ebooks/${id}/pages/${pageNumber}/text`),
  updatePageText: (
    id: string,
    pageNumber: number,
    editedText: string | null,
    expectedRevision: number,
  ) =>
    request<EbookPageText>(`/api/ebooks/${id}/pages/${pageNumber}/text`, {
      method: "PATCH",
      mutation: true,
      body: JSON.stringify({ editedText, expectedRevision }),
    }),
  mediaOverlays: (id: string, pageNumber: number) =>
    request<MediaOverlays>(`/api/ebooks/${id}/pages/${pageNumber}/media-overlays`),
  updateMediaOverlays: (
    id: string,
    pageNumber: number,
    expectedRevision: number,
    items: MediaOverlayInput[],
  ) =>
    request<MediaOverlays>(`/api/ebooks/${id}/pages/${pageNumber}/media-overlays`, {
      method: "PUT",
      mutation: true,
      body: JSON.stringify({ expectedRevision, items }),
    }),
  startMediaOverlayUpload: (
    id: string,
    pageNumber: number,
    input: { kind: "audio_asset" | "video_asset"; fileName: string; contentType: string; size: number },
  ) =>
    request<MediaOverlayUploadStartResponse>(`/api/ebooks/${id}/pages/${pageNumber}/media-overlays/upload`, {
      method: "POST",
      mutation: true,
      body: JSON.stringify(input),
    }),
  completeMediaOverlayUpload: (
    id: string,
    pageNumber: number,
    kind: "audio_asset" | "video_asset",
    objectKey: string,
  ) =>
    request<MediaOverlays>(`/api/ebooks/${id}/pages/${pageNumber}/media-overlays/upload/complete`, {
      method: "POST",
      mutation: true,
      body: JSON.stringify({ kind, objectKey }),
    }),
  searchPageText: (id: string, query: string) =>
    request<EbookPageSearchResponse>(
      `/api/ebooks/${id}/pages/search?q=${encodeURIComponent(query)}`,
    ),
  aiReview: (id: string) =>
    request<AiReview>(`/api/ebooks/${id}/ai-review`),
  startAiReview: (
    id: string,
    scope: AiReviewScope,
    pageNumber?: number,
    pageNumbers?: number[],
  ) =>
    request<AiReview>(`/api/ebooks/${id}/ai-review`, {
      method: "POST",
      mutation: true,
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({
        scope,
        ...(pageNumber === undefined ? {} : { pageNumber }),
        ...(pageNumbers === undefined ? {} : { pageNumbers }),
      }),
    }),
  applyAiReview: (
    id: string,
    pageNumber: number,
    jobId: string,
    expectedRevision: number,
  ) =>
    request<EbookPageText>(
      `/api/ebooks/${id}/pages/${pageNumber}/ai-review/apply`,
      {
        method: "POST",
        mutation: true,
        body: JSON.stringify({ jobId, expectedRevision }),
      },
    ),
  processing: (id: string) => request<Processing>(`/api/ebooks/${id}/processing`),
  retryProcessing: (id: string) =>
    request<{ status: string }>(`/api/ebooks/${id}/processing/retry`, {
      method: "POST",
      mutation: true,
      headers: { "idempotency-key": crypto.randomUUID() },
    }),
  startUpload: (id: string, input: {
    fileName: string;
    contentType: string;
    size: number;
    sha256: string;
    autoOcrScan: boolean;
  }) =>
    request<UploadStartResponse>(`/api/ebooks/${id}/uploads`, {
      method: "POST",
      mutation: true,
      body: JSON.stringify(input),
    }),
  completeUpload: (
    ebookId: string,
    uploadId: string,
    workerUploadId: string,
    parts: Array<{ partNumber: number; etag: string }>,
  ) =>
    request<{ status: string }>(`/api/ebooks/${ebookId}/uploads/${uploadId}/complete`, {
      method: "POST",
      mutation: true,
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ workerUploadId, parts }),
    }),
};
